import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  RefreshCw, ExternalLink, Copy, CheckCircle2, AlertTriangle, XCircle, Info,
} from "lucide-react";

export default function MerchantFeed() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgAudit, setImgAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/merchant-feed/diagnostics")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load feed diagnostics"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const runImageAudit = () => {
    setAuditing(true);
    api.get("/admin/image-audit")
      .then(({ data }) => {
        setImgAudit(data);
        if (data.healthy) toast.success("All product images resolve correctly");
        else toast.warning(`${data.products_with_broken_images + data.products_missing_images} product(s) need attention`);
      })
      .catch(() => toast.error("Image audit failed"))
      .finally(() => setAuditing(false));
  };

  const copyUrl = () => {
    if (!data?.feed_url) return;
    navigator.clipboard.writeText(data.feed_url);
    toast.success("Feed URL copied");
  };

  const fmtTime = (iso) => {
    if (!iso) return "Not generated yet";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <AdminLayout
      title="Merchant Feed"
      actions={
        <button
          onClick={load}
          data-testid="feed-refresh-btn"
          className="inline-flex items-center gap-2 text-sm px-3 py-2 border border-zinc-200 rounded hover:bg-zinc-50"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      }
    >
      {loading && !data ? (
        <div className="text-zinc-500">Loading diagnostics…</div>
      ) : !data ? (
        <div className="text-zinc-500">No data.</div>
      ) : (
        <div className="space-y-8" data-testid="merchant-feed-panel">
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testid="stat-active" label="Active products" value={data.active_products} icon={Info} tone="zinc" />
            <StatCard testid="stat-included" label="Included in feed" value={data.included} icon={CheckCircle2} tone="emerald" />
            <StatCard testid="stat-skipped" label="Skipped" value={data.skipped} icon={XCircle} tone={data.skipped ? "red" : "zinc"} />
            <StatCard testid="stat-warnings" label="With warnings" value={data.warnings} icon={AlertTriangle} tone={data.warnings ? "amber" : "zinc"} />
          </div>

          {/* Feed info */}
          <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Feed URL</span>
              <a
                href={data.feed_url}
                target="_blank"
                rel="noreferrer"
                data-testid="feed-url-link"
                className="text-sm text-red-600 hover:text-red-500 font-mono break-all inline-flex items-center gap-1"
              >
                {data.feed_url} <ExternalLink size={12} />
              </a>
              <button onClick={copyUrl} data-testid="feed-copy-btn" className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded hover:bg-zinc-50">
                <Copy size={12} /> Copy
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <InfoRow label="Feed validation">
                {data.feed_valid_xml
                  ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 size={14} /> Valid XML</span>
                  : <span className="inline-flex items-center gap-1 text-red-600"><XCircle size={14} /> Invalid</span>}
              </InfoRow>
              <InfoRow label="Last feed generation">{fmtTime(data.feed_last_generated)}</InfoRow>
              <InfoRow label="Refresh behavior">
                <span className="text-zinc-700">Dynamic · {data.cache_ttl_seconds}s cache</span>
              </InfoRow>
            </div>
            <div className="flex items-start gap-2 text-xs text-zinc-500 bg-zinc-50 rounded p-3">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span>
                The feed is generated dynamically from live product data with a short {data.cache_ttl_seconds}-second cache —
                <strong className="text-zinc-700"> no manual regeneration is required</strong>. New, edited, and archived
                products are reflected automatically (within {data.cache_ttl_seconds}s). GTIN is intentionally omitted for
                aftermarket parts (identified via brand + MPN).
              </span>
            </div>
          </div>

          {/* Shipping labels */}
          <div className="bg-white border border-zinc-200 rounded-lg p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">Shipping labels</div>
            <div className="flex flex-wrap gap-2" data-testid="feed-label-counts">
              {Object.entries(data.shipping_label_counts || {}).map(([k, v]) => (
                <span key={k} className="text-xs bg-zinc-100 text-zinc-700 rounded px-2.5 py-1 font-medium">
                  {k}: <span className="font-mono">{v}</span>
                </span>
              ))}
              {Object.entries(data.category_counts || {}).map(([k, v]) => (
                <span key={k} className="text-xs bg-blue-50 text-blue-700 rounded px-2.5 py-1 font-medium">
                  {k}: <span className="font-mono">{v}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Skipped products */}
          <FeedTable
            title="Skipped products (not in feed)"
            testid="feed-skipped-table"
            rows={data.skipped_details}
            tone="red"
            emptyText="No products are being skipped. 🎉"
          />

          {/* Warning products */}
          <FeedTable
            title="Products with warnings (included, but improvable)"
            testid="feed-warnings-table"
            rows={data.warning_details}
            tone="amber"
            emptyText="No feed-quality warnings."
          />

          {/* Image integrity audit (live HTTP check) */}
          <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="image-audit-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Image integrity audit</div>
              <button
                onClick={runImageAudit}
                data-testid="image-audit-run-btn"
                disabled={auditing}
                className="inline-flex items-center gap-2 text-sm px-3 py-2 border border-zinc-200 rounded hover:bg-zinc-50 disabled:opacity-50"
              >
                <RefreshCw size={14} className={auditing ? "animate-spin" : ""} /> {auditing ? "Auditing…" : "Run image audit"}
              </button>
            </div>
            <p className="text-xs text-zinc-500 mb-4">
              Live check of every active product's images (primary + additional). Detects broken URLs, missing,
              inaccessible, insecure (non-HTTPS) and placeholder images. Read-only — never edits or removes products.
            </p>
            {!imgAudit ? (
              <div className="text-sm text-zinc-400">Run the audit to verify all storefront &amp; feed images resolve.</div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                  <StatCard testid="img-stat-ok" label="Images OK" value={`${imgAudit.ok_images}/${imgAudit.total_images}`} icon={CheckCircle2} tone="emerald" />
                  <StatCard testid="img-stat-broken-primary" label="Broken primary" value={imgAudit.products_with_broken_primary} icon={XCircle} tone={imgAudit.products_with_broken_primary ? "red" : "zinc"} />
                  <StatCard testid="img-stat-broken" label="Products w/ broken img" value={imgAudit.products_with_broken_images} icon={AlertTriangle} tone={imgAudit.products_with_broken_images ? "amber" : "zinc"} />
                  <StatCard testid="img-stat-missing" label="Missing images" value={imgAudit.products_missing_images} icon={Info} tone={imgAudit.products_missing_images ? "red" : "zinc"} />
                </div>
                {imgAudit.healthy ? (
                  <div className="text-sm text-emerald-600 inline-flex items-center gap-2"><CheckCircle2 size={16} /> All product images resolve correctly. 🎉</div>
                ) : (
                  <div className="overflow-hidden border border-zinc-200 rounded-lg" data-testid="image-audit-table">
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                        <tr>
                          <th className="text-left px-5 py-2">Product</th>
                          <th className="text-left px-3 py-2">SKU</th>
                          <th className="text-left px-3 py-2">Issue</th>
                          <th className="text-right px-5 py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {[...(imgAudit.affected_details || []), ...(imgAudit.missing_details || [])].map((r) => (
                          <tr key={r.id} data-testid={`image-audit-row-${r.sku || r.id}`}>
                            <td className="px-5 py-3 text-zinc-800">{r.name || "—"}{r.primary_broken && <span className="ml-2 text-[10px] uppercase font-bold text-red-600">primary</span>}</td>
                            <td className="px-3 py-3 font-mono text-zinc-600">{r.sku || "—"}</td>
                            <td className="px-3 py-3">
                              <div className="flex flex-col gap-1">
                                {(r.reasons || []).map((reason, i) => (
                                  <span key={i} className="text-[11px] text-red-700">
                                    {typeof reason === "string" ? reason : `${reason.reason}${reason.primary ? " (primary)" : ""}`}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-5 py-3 text-right whitespace-nowrap">
                              {r.slug && (
                                <a href={`/product/${r.slug}`} target="_blank" rel="noreferrer" className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1 mr-3">
                                  View <ExternalLink size={11} />
                                </a>
                              )}
                              {r.id && (
                                <Link to={`/admin/products/${r.id}`} className="text-xs text-red-600 hover:text-red-500 font-medium">Fix image</Link>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function StatCard({ label, value, icon: Icon, tone, testid }) {
  const tones = {
    zinc: "text-zinc-700", emerald: "text-emerald-600", red: "text-red-600", amber: "text-amber-600",
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">{label}</span>
        <Icon size={16} className={tones[tone]} />
      </div>
      <div className={`text-3xl font-display font-medium mt-2 ${tones[tone]}`}>{value ?? "—"}</div>
    </div>
  );
}

function InfoRow({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function FeedTable({ title, rows, tone, emptyText, testid }) {
  const badge = tone === "red" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";
  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
        {title} · {rows?.length || 0}
      </div>
      {!rows || rows.length === 0 ? (
        <div className="px-5 py-6 text-sm text-zinc-500">{emptyText}</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-5 py-2">Product</th>
              <th className="text-left px-3 py-2">SKU</th>
              <th className="text-left px-3 py-2">Reasons</th>
              <th className="text-right px-5 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`feed-row-${r.sku || r.id}`}>
                <td className="px-5 py-3 text-zinc-800">{r.name || "—"}</td>
                <td className="px-3 py-3 font-mono text-zinc-600">{r.sku || "—"}</td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(r.reasons || r.warnings || []).map((reason, i) => (
                      <span key={i} className={`text-[10px] uppercase tracking-wide font-bold rounded px-1.5 py-0.5 ${badge}`}>{reason}</span>
                    ))}
                  </div>
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {r.slug && (
                    <a href={`/product/${r.slug}`} target="_blank" rel="noreferrer" className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1 mr-3">
                      View <ExternalLink size={11} />
                    </a>
                  )}
                  {r.id && (
                    <Link to={`/admin/products/${r.id}`} className="text-xs text-red-600 hover:text-red-500 font-medium">Edit</Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
